import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@grebban/style-system-react';
import Text from 'components/Text';

const Wrapper = styled('div')`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 10px;
    pointer-events: initial;
`;

const CategoryButton = styled('button')`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    color: var(--button-tertiery-text);
    transition: color var(--transition-primary-fast);
    margin-bottom: 3px;

    &:hover {
        color: var(--button-tertiery-text-hover);
    }

    &[data-active='true'] {
        color: var(--button-tertiery-text-active);
    }
`;

const CategoryFilterButtons = ({ filters, selectedFilters, hasCategoryFilter }) => {
    const history = useHistory();
    const { t } = useTranslation();

    const categoryFilterKey = hasCategoryFilter ? 'subcategory' : 'categories';
    const categoryUrlParam = hasCategoryFilter ? 'subcategory' : 'category';

    const categoryFilter = filters?.find((f) => f.key === categoryFilterKey);

    if (!categoryFilter?.values?.length) {
        return null;
    }

    const topLevelValues = categoryFilter.values.filter((v) => !v.value.includes('>'));

    if (!topLevelValues.length) {
        return null;
    }

    const activeCategory = selectedFilters?.find((f) => f.key === categoryFilterKey)?.value;

    const handleAllClick = useCallback(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete(categoryUrlParam);
        url.searchParams.delete('page');
        history.replace(`${url.pathname}?${url.searchParams.toString().replace(/\+/g, '%20')}`);
    }, [history, categoryUrlParam]);

    const handleCategoryClick = useCallback(
        (value) => {
            const url = new URL(window.location.href);

            if (value === activeCategory) {
                url.searchParams.delete(categoryUrlParam);
            } else {
                url.searchParams.set(categoryUrlParam, value);
            }

            url.searchParams.delete('page');
            history.replace(`${url.pathname}?${url.searchParams.toString().replace(/\+/g, '%20')}`);
        },
        [history, activeCategory, categoryUrlParam],
    );

    const isAllActive = !activeCategory;

    return (
        <Wrapper>
            <CategoryButton type="button" data-active={isAllActive} onClick={handleAllClick}>
                <Text typography="Primary/15_120_510_-1" $as="span">
                    {t('category.filter.all_products')}
                    {topLevelValues.length > 0 && `,\u00A0`}
                </Text>
            </CategoryButton>
            {topLevelValues.map((item, index) => (
                <CategoryButton
                    key={item.value}
                    type="button"
                    data-active={item.value === activeCategory}
                    onClick={() => handleCategoryClick(item.value)}
                >
                    <Text typography="Primary/15_120_510_-1" $as="span">
                        {item.label || item.value}
                        {index < topLevelValues.length - 1 && `,\u00A0`}
                    </Text>
                </CategoryButton>
            ))}
        </Wrapper>
    );
};

export default CategoryFilterButtons;
